// src/components/CardAgendaActions.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom"; // react-router-dom v5
import PERMISSIONS, { hasUmatPermission } from "../utils/wkCommon";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import { getUser } from "../utils/auth";
import { isZh } from "../utils/language";

/**
 * CardAgendaActions
 * Props:
 *  - agenda: object (may be Strapi shape with .attributes or flat)
 *  - className: optional extra class
 *
 * Behavior:
 *  - render action buttons, each guarded by permission check
 *  - first button: Presensi -> /presensi/:documentId (PERMISSIONS.EVENTPARTICIPANT_READ)
 *
 * Notes:
 *  - Uses sync hasUmatPermission(...) to quickly render when cache present.
 *  - Also performs async check to update UI when cache wasn't ready.
 */

const css = `
.card-agenda-actions{display:flex;gap:0.5rem;flex-wrap:wrap;align-items:center}
.ca-btn{font-weight:700;padding:0.45rem 0.75rem;border-radius:0.5rem;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:0.375rem;font-size:0.9rem /* 0.9rem intentionally */;}
.ca-primary{background:#0b63a8;color:#fff;border:none}
.ca-ghost{background:transparent;color:#0b63a8;border:1px solid rgba(11,99,168,0.12)}
`;

export default function CardAgendaActions({ agenda, className = "" }) {
  const user = getUser();
  const email = user?.email ?? null;
 
  const documentId = agenda?.documentId ?? null;
  const hasPreRegForm =
    !!(
      agenda?.agenda_pre_reg_form ||
      agenda?.agendaPreRegForm ||
      agenda?.allowPreRegistration
    );
    
  // sync quick-checks (from cache)
  const quickCanReadParticipants = hasUmatPermission(email, PERMISSIONS.EVENTPARTICIPANT_READ);
  //const quickCanEditAgenda = hasUmatPermission(email, PERMISSIONS.AGENDA_WRITE);
  //const quickCanManagePrereg = hasUmatPermission(email, PERMISSIONS.AGENDA_PREREG_WRITE);

  // states updated by async checks (will set true/false)
  const [canReadParticipants, setCanReadParticipants] = useState(Boolean(quickCanReadParticipants));
  const [canReadAgendaRundown, setCanReadAgendaRundown] = useState(false);
  const [canReadAgendaPersonnel, setCanReadAgendaPersonnel] = useState(false);
  const [canReadAgendaExpense, setCanReadAgendaExpense] = useState(false);
  const [canEditAgendaPreRegForm, setCanEditAgendaPreRegForm] = useState(false);
  const [canReadPreReg, setCanReadPreReg] = useState(false);

  //const [canEditAgenda, setCanEditAgenda] = useState(Boolean(quickCanEditAgenda));
  //const [canManagePrereg, setCanManagePrereg] = useState(Boolean(quickCanManagePrereg));

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        // only call async check if not already true (avoid extra calls)
        if (!quickCanReadParticipants) {
          const ok = await hasUmatPermissionAsync(email, PERMISSIONS.EVENTPARTICIPANT_READ);
          if (mounted) setCanReadParticipants(Boolean(ok));
        }

        const a = await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_RUNDOWN_READ);
        if (mounted) setCanReadAgendaRundown(Boolean(a));

        const b = await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_PERSONNEL_READ);
        if (mounted) setCanReadAgendaPersonnel(Boolean(b));

        const c = await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_EXPENSE_READ);
        if (mounted) setCanReadAgendaExpense(Boolean(c));

        const d = await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_PREREGFORM_EDITOR);
        if (mounted) setCanEditAgendaPreRegForm(Boolean(d));

        const e = await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_PREREG_READ);
        if (mounted) setCanReadPreReg(Boolean(e));

        // if (!quickCanEditAgenda) {
        //   const ok = await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_WRITE);
        //   if (mounted) setCanEditAgenda(Boolean(ok));
        // }
        // if (!quickCanManagePrereg) {
        //   const ok = await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_PREREG_WRITE);
        //   if (mounted) setCanManagePrereg(Boolean(ok));
        // }
      } catch (e) {
        // ignore; keep current states
        console.warn("CardAgendaActions: permission load failed", e);
      }
    }
    load();
    return () => { mounted = false; };
  }, [email]); // re-run when user/email changes

  // helper for rendering link or disabled button when documentId missing
  function renderLinkOrDisabled({ to, label, title, primary = false, enabled = true }) {

    if (!enabled) return null;

    const cls = `ca-btn ${primary ? "ca-primary" : "ca-ghost"}`;

    return (
      <Link to={to} className={cls} title={title}>
        {label}
      </Link>
    );
  }

  return (
    <div className={`card-agenda-actions ${className}`} role="group" aria-label="Agenda actions">
      <style dangerouslySetInnerHTML={{ __html: css }} />

      {/* PRESENSI: requires EVENTPARTICIPANT_READ */}
      {renderLinkOrDisabled({
        to: documentId ? `/presensi/${encodeURIComponent(documentId)}` : "#",
        label: isZh() ? "簽到資料" : "Data Presensi",
        title: isZh() 
            ? "管理參加者簽到資料（開啟簽到頁面）" 
            : "Kelola presensi peserta (membuka halaman presensi)",
        primary: false,
        enabled: Boolean(canReadParticipants && documentId),
      })}

      {/* BUKU-AGENDA: requires AGENDA_RUNDOWN_READ / AGENDA_PERSONNEL_READ / AGENDA_EXPENSE_READ */}
      {renderLinkOrDisabled({
        to: documentId ? `/agendabook/${encodeURIComponent(documentId)}` : "#",
        label: isZh() ? "議程簿" : "Buku Agenda",
        title: isZh() 
            ? "管理活動流程、辦事人員名單及收支記錄（開啟議程簿）" 
            : "Kelola jadwal acara, daftar petugas dan catatan donasi-biaya (membuka buku agenda)",
        primary: false,
        enabled: Boolean((canReadAgendaRundown || canReadAgendaPersonnel || canReadAgendaExpense) && documentId),
      })}

      {/* BUKU-AGENDA: requires AGENDA_RUNDOWN_READ / AGENDA_PERSONNEL_READ / AGENDA_EXPENSE_READ */}
      {renderLinkOrDisabled({
        to: documentId ? `/abr/${encodeURIComponent(documentId)}` : "#",
        label: isZh() ? "報表預覽" : "Laporan Agenda",
        title: isZh() 
            ? "查看流程、辦事人員名單及收支記錄的報表" 
            : "Laporan jadwal acara, daftar petugas dan catatan keuangan",
        primary: false,
        enabled: Boolean((canReadAgendaRundown || canReadAgendaPersonnel || canReadAgendaExpense) && documentId),
      })}

      {/* BUKU-AGENDA: requires AGENDA_RUNDOWN_READ / AGENDA_PERSONNEL_READ / AGENDA_EXPENSE_READ */}
      {renderLinkOrDisabled({
        to: documentId ? `/PreRegFormSetup/${encodeURIComponent(documentId)}` : "#",
        label: isZh() ? "報名表設定" : "Setup Form Registrasi",
        title: isZh() 
            ? "設計、修改及預覽報名表" 
            : "Rancang, modifikasi dan preview form pendaftaran/registrasi.",
        primary: false,
        enabled: Boolean(canEditAgendaPreRegForm && documentId),
      })}

      {renderLinkOrDisabled({
        to: documentId ? `/prereglist/${encodeURIComponent(documentId)}` : "#",
        label: isZh() ? "預先報名名單" : "List Nama Pra-Registrasi",
        title: isZh() 
            ? "班程預先報名名單" 
            : "Form pendaftaran kelas/kegiatan.",
        primary: false,
        enabled: Boolean(canReadPreReg &&　hasPreRegForm && documentId),
      })}

      {/* EDIT AGENDA: requires AGENDA_WRITE */}
{/*       {renderLinkOrDisabled({
        to: documentId ? `/agenda/edit/${encodeURIComponent(documentId)}` : "#",
        label: "Edit",
        title: "Edit agenda",
        primary: true,
        enabled: Boolean(canEditAgenda && documentId),
      })} */}

      {/* PRE-REGISTRATION MANAGE: requires AGENDA_PREREG_WRITE */}
{/*       {renderLinkOrDisabled({
        to: documentId ? `/agenda/${encodeURIComponent(documentId)}/prereg` : "#",
        label: "Pre-registration",
        title: "Kelola Pre-registration",
        primary: false,
        enabled: Boolean(canManagePrereg && documentId),
      })} */}

      {/* Example: more actions can be added here similarly with permission checks */}
    </div>
  );
}

CardAgendaActions.propTypes = {
  agenda: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
  className: PropTypes.string,
};
